import { apiCall, apiStream } from '@/lib/api';
import {
  createInfiniteQuery,
  createMutation,
  createQuery,
  useQueryClient,
} from '@tanstack/solid-query';
import {
  type CreateMachineBody,
  type ListJobsQuery,
  type SampleRange,
  createRunnerMachine,
  deleteRunnerMachine,
  getRunnerMachine,
  getRunnerMachineSamples,
  getRunnerOverview,
  listRunnerJobs,
  listRunnerMachines,
  listRunnerQueue,
  listRunners,
  rotateRunnerMachineToken,
  runnerMonitorEventsStream,
} from '@uchat-team-management/shared';
import { onCleanup, onMount } from 'solid-js';
import { parseSse } from '../releases/sse';

const ROOT = 'runner-monitor';
/** SSE drives updates; this poll only covers a dropped stream. */
const FALLBACK_REFETCH_MS = 30_000;
const STREAM_RETRY_MS = 5_000;

const live = { refetchInterval: FALLBACK_REFETCH_MS, staleTime: 5_000 } as const;

export const useRunnerOverview = () =>
  createQuery(() => ({
    queryKey: [ROOT, 'overview'],
    queryFn: () => apiCall(getRunnerOverview, {}),
    ...live,
  }));

export const useRunnerMachines = () =>
  createQuery(() => ({
    queryKey: [ROOT, 'machines'],
    queryFn: () => apiCall(listRunnerMachines, {}),
    ...live,
  }));

export const useRunnerMachine = (machineId: () => string) =>
  createQuery(() => ({
    queryKey: [ROOT, 'machine', machineId()],
    queryFn: () => apiCall(getRunnerMachine, { params: { machineId: machineId() } }),
    ...live,
  }));

export type SampleWindow = { range: SampleRange } | { from: string; to: string };

export const useMachineSamples = (machineId: () => string, window: () => SampleWindow) =>
  createQuery(() => ({
    queryKey: [ROOT, 'samples', machineId(), window()],
    queryFn: () =>
      apiCall(getRunnerMachineSamples, { params: { machineId: machineId() }, query: window() }),
    ...live,
    // Keep the old line on screen while a new range loads.
    placeholderData: (prev) => prev,
  }));

export const useRunners = () =>
  createQuery(() => ({
    queryKey: [ROOT, 'runners'],
    queryFn: () => apiCall(listRunners, {}),
    ...live,
  }));

export const useRunnerQueue = () =>
  createQuery(() => ({
    queryKey: [ROOT, 'queue'],
    queryFn: () => apiCall(listRunnerQueue, {}),
    ...live,
  }));

export type JobsFilters = Omit<ListJobsQuery, 'cursor' | 'limit'> & { limit?: number };

export const useRunnerJobs = (filters: () => JobsFilters) =>
  createInfiniteQuery(() => ({
    queryKey: [ROOT, 'jobs', filters()],
    queryFn: ({ pageParam }) =>
      apiCall(listRunnerJobs, {
        query: { limit: 50, ...filters(), ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
    ...live,
  }));

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [ROOT] });
};

export const useCreateMachine = () => {
  const invalidate = useInvalidate();
  return createMutation(() => ({
    mutationFn: (body: CreateMachineBody) => apiCall(createRunnerMachine, { body }),
    onSuccess: invalidate,
  }));
};

export const useRotateMachineToken = (machineId: () => string) =>
  createMutation(() => ({
    mutationFn: () => apiCall(rotateRunnerMachineToken, { params: { machineId: machineId() } }),
  }));

export const useDeleteMachine = (machineId: () => string) => {
  const invalidate = useInvalidate();
  return createMutation(() => ({
    mutationFn: () => apiCall(deleteRunnerMachine, { params: { machineId: machineId() } }),
    onSuccess: invalidate,
  }));
};

/**
 * Holds the runner-monitor SSE stream open while the page is mounted and
 * refetches every runner-monitor query on each `changed`; reconnects on drop.
 */
export const useRunnerLive = () => {
  const invalidate = useInvalidate();
  onMount(() => {
    const ctrl = new AbortController();
    onCleanup(() => ctrl.abort());
    void (async () => {
      while (!ctrl.signal.aborted) {
        try {
          const res = await apiStream(runnerMonitorEventsStream, { signal: ctrl.signal });
          const reader = res.ok ? res.body?.getReader() : undefined;
          const decoder = new TextDecoder();
          let buffer = '';
          for (let r = await reader?.read(); r && !r.done; r = await reader?.read()) {
            const parsed = parseSse(buffer + decoder.decode(r.value, { stream: true }));
            buffer = parsed.rest;
            if (parsed.events.includes('changed')) void invalidate();
          }
        } catch {
          // Network drop or abort — retry below unless aborted; the 30s poll covers the gap.
        }
        await new Promise((resolve) => setTimeout(resolve, STREAM_RETRY_MS));
      }
    })();
  });
};
